import { useI18n } from "../../lib/i18n.jsx";
import { docUrl } from "../../lib/content.js";
import {
  Bed,
  Utensils,
  MessageCircle,
  Heart,
  Phone,
  Camera,
  FileText,
  Download,
  ListChecks,
  Calendar,
  AlertCircle,
  Sparkles,
  ClipboardList,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import Reveal from "../Reveal.jsx";

const COMM_ICONS = [Camera, Phone, MessageCircle];

export default function ForParents() {
  const { t } = useI18n();
  const p = t.parents;
  const s = t.info;

  return (
    <section
      id="dla-rodzicow"
      data-testid="parents-section"
      className="bg-cream py-20 md:py-28"
    >
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <Reveal as="div" className="text-center max-w-2xl mx-auto mb-10">
          <p className="font-handwritten text-summer text-2xl md:text-3xl mb-2">
            {p.kicker}
          </p>
          <h2
            className="font-display font-bold text-winter leading-tight mb-3"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {p.title}
          </h2>
          <p className="text-lg text-winter/65">{p.subtitle}</p>
        </Reveal>

        <Tabs defaultValue="comfort" className="w-full">
          <TabsList
            className="mx-auto mb-8 bg-white border border-winter/10 rounded-full p-1 h-auto flex w-fit gap-1 flex-wrap justify-center"
            data-testid="for-parents-tabs"
          >
            <TabsTrigger
              value="comfort"
              data-testid="for-parents-tab-comfort"
              className="rounded-full px-5 py-2 text-sm font-display font-bold data-[state=active]:bg-summer data-[state=active]:text-cream data-[state=active]:shadow-md text-winter/65"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {p.tab_comfort}
            </TabsTrigger>
            <TabsTrigger
              value="documents"
              data-testid="for-parents-tab-documents"
              className="rounded-full px-5 py-2 text-sm font-display font-bold data-[state=active]:bg-summer data-[state=active]:text-cream data-[state=active]:shadow-md text-winter/65"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              {p.tab_documents}
            </TabsTrigger>
          </TabsList>

          {/* COMFORT */}
          <TabsContent value="comfort" className="m-0">
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="card-soft p-7" data-testid="parents-accommodation">
                <div className="w-12 h-12 rounded-full bg-summer/10 flex items-center justify-center mb-4">
                  <Bed className="w-6 h-6 text-summer" />
                </div>
                <h3 className="font-display font-bold text-winter text-2xl mb-2">
                  {p.accommodation_title}
                </h3>
                <p className="text-sm text-winter/60 mb-4">{p.accommodation_subtitle}</p>
                <ul className="space-y-2">
                  {p.accommodation.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-winter/85"
                    >
                      <span className="text-summer mt-1">•</span> {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card-soft p-7" data-testid="parents-meals">
                <div className="w-12 h-12 rounded-full bg-summer/10 flex items-center justify-center mb-4">
                  <Utensils className="w-6 h-6 text-summer" />
                </div>
                <h3 className="font-display font-bold text-winter text-2xl mb-2">
                  {p.meals_title}
                </h3>
                <p className="text-sm text-winter/60 mb-4">{p.meals_subtitle}</p>
                <ul className="space-y-2.5">
                  {p.meals.map((m, i) => (
                    <li key={i}>
                      <p className="font-display font-bold text-winter text-sm">
                        {m.meal}
                      </p>
                      <p className="text-xs text-winter/60">{m.body}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card-soft p-7" data-testid="parents-comms">
                <div className="w-12 h-12 rounded-full bg-summer/10 flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-summer" />
                </div>
                <h3 className="font-display font-bold text-winter text-2xl mb-2">
                  {p.comms_title}
                </h3>
                <p className="text-sm text-winter/60 mb-4">{p.comms_subtitle}</p>
                <ul className="space-y-3">
                  {p.comms.map((c, i) => {
                    const Icon = COMM_ICONS[i] || Camera;
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <Icon className="w-4 h-4 text-summer mt-1 shrink-0" />
                        <div>
                          <p className="font-display font-bold text-winter text-sm">
                            {c.title}
                          </p>
                          <p className="text-xs text-winter/60">{c.body}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="card-soft mt-6 p-5 bg-summer/5 border-summer/20 flex items-start gap-3">
              <Heart className="w-5 h-5 text-summer mt-0.5 shrink-0" />
              <p className="text-winter/80 text-sm">
                <strong>{p.diets_strong}</strong> {p.diets_body}
              </p>
            </div>
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="documents" className="m-0">
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-summer/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-summer" />
                  </div>
                  <h3 className="font-display font-bold text-winter text-2xl">
                    {s.documents_title}
                  </h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="documents-list">
                  {s.documents.map((d, i) => (
                    <a
                      key={i}
                      href={docUrl(d.file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`document-${i}`}
                      className="card-soft p-4 flex items-center gap-3 group"
                    >
                      <div className="w-11 h-11 rounded-2xl bg-summer/10 group-hover:bg-summer flex items-center justify-center transition-colors shrink-0">
                        <Download className="w-5 h-5 text-summer group-hover:text-cream transition-colors" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-display font-bold text-winter text-sm">
                          {d.name}
                        </p>
                        <p className="text-xs text-winter/60 truncate">{d.desc}</p>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="mt-4 card-soft p-4 bg-sun/10 border-sun/30 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-sun mt-0.5 shrink-0" />
                  <p className="text-winter/80 text-sm">
                    {s.card_alert_prefix}{" "}
                    <strong>{s.card_alert_strong}</strong>{" "}
                    {s.card_alert_suffix}
                  </p>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-summer/10 flex items-center justify-center">
                    <ListChecks className="w-5 h-5 text-summer" />
                  </div>
                  <h3 className="font-display font-bold text-winter text-2xl">
                    {s.packing_title}
                  </h3>
                </div>
                <ul
                  className="card-soft p-5 divide-y divide-winter/10"
                  data-testid="packing-list"
                >
                  {s.packing.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0"
                    >
                      <span className="font-mono text-xs font-bold text-summer w-7 mt-0.5 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-winter/85 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
